import { useCallback, useState } from 'react'
import type { ListItem, ShoppingList } from '@domain/list'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { useLive } from '@ui/lists/use-live'

/**
 * O que dá para fazer com uma lista, a partir da aba dela.
 *
 * ## Só apaga vazia, e a vacuidade sai dos ITENS — não do contador
 *
 * `ShoppingList` carrega `itemCount`, e usá-lo aqui seria a resposta óbvia e
 * errada: ele é desnormalizado por Cloud Function, e o próprio conversor avisa
 * que vale **zero enquanto ela não rodou**. Uma lista cheia apareceria vazia, e
 * o botão que some quando há itens estaria aceso justamente no caso em que
 * apagar perde coisa.
 *
 * Então a folha assina os itens da lista. Vem de brinde o que uma leitura única
 * não daria: se a outra pessoa esvaziar a lista com esta folha aberta, o botão
 * acende sozinho — é a mesma exigência de tempo real do resto da tela.
 *
 * ## "Vazia" é o que a TELA chama de vazia
 *
 * `observeItems` devolve os itens vivos; os limpos não vêm. É a mesma conta que
 * escreve "Lista vazia. Escreva o que falta no campo acima." logo abaixo — e
 * duas definições de vazio na mesma tela seriam uma a mais.
 *
 * ## A última lista não se apaga
 *
 * *"Vamos sempre ter, pelo menos, uma, que vai ser a padrão."* Sem esta trava a
 * casa cai no estado de "nenhuma lista ainda", que existe para quem nunca criou
 * nenhuma — não para quem acabou de apagar a única que tinha.
 *
 * ## Sem saber, não apaga
 *
 * Se a assinatura falhar, a folha não sabe se a lista está vazia — e o botão
 * não aparece. Apagar com base num palpite é o que a trava existe para impedir.
 *
 * ## O motivo de o botão não poder aparecer aceso e recusar
 *
 * Um botão que devolve "não dá" é uma promessa quebrada — a mesma regra que faz
 * a thread de comentários não oferecer editar o que a regra nega. Aqui ele
 * some, e **o lugar dele explica por quê**: sem a frase, quem segurou a aba
 * para apagar não teria como saber o que fazer em seguida.
 */
export function MenuDaLista({
  lista,
  ehAUnica,
  onFechar,
}: {
  lista: ShoppingList
  ehAUnica: boolean
  onFechar: () => void
}) {
  const { listRepository } = useFinanceRepositories()
  const assinar = useCallback(
    (onChange: (i: ListItem[]) => void, onError: (e: unknown) => void) =>
      listRepository.observeItems(lista.id, onChange, onError),
    [listRepository, lista.id],
  )
  const itens = useLive<ListItem>(assinar)
  const [apagando, setApagando] = useState(false)
  const [falhou, setFalhou] = useState(false)

  const nome = `${lista.icon} ${lista.name}`.trim()
  // `null` enquanto não se sabe — e não saber inclui a assinatura ter falhado.
  // Sem contar os itens não dá para afirmar que a lista está vazia, e apagar
  // com base num palpite é exatamente o que a trava existe para impedir.
  const quantos = itens.status === 'loaded' ? itens.items.length : null

  return (
    <FolhaDeBaixo titulo={nome} onFechar={onFechar}>
      <h2 className="titulo mb-3 text-base">{nome}</h2>

      {itens.status === 'loading' && (
        <p className="text-sm text-[var(--text-muted)]">Conferindo a lista…</p>
      )}
      {itens.status === 'failed' && (
        <p className="text-sm text-[var(--text-muted)]">
          Não deu para conferir se a lista está vazia, então não dá para apagar agora.
        </p>
      )}

      {quantos !== null && ehAUnica && (
        <p className="text-sm text-[var(--text-muted)]">
          É a única lista da casa. Crie outra antes de apagar esta.
        </p>
      )}

      {quantos !== null && !ehAUnica && quantos > 0 && (
        <p className="text-sm text-[var(--text-muted)]">
          {quantos === 1
            ? 'Esta lista tem 1 item. Ela precisa estar vazia para ser apagada.'
            : `Esta lista tem ${String(quantos)} itens. Ela precisa estar vazia para ser apagada.`}
        </p>
      )}

      {quantos === 0 && !ehAUnica && (
        <p className="text-sm text-[var(--text-muted)]">
          A lista some da tela. O que já foi comprado nela continua guardado.
        </p>
      )}

      {falhou && (
        <p role="alert" className="mt-3 text-xs text-[var(--text-muted)]">
          Não foi possível apagar agora.
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {quantos === 0 && !ehAUnica && (
          <Botao
            type="button"
            tom="perigo"
            disabled={apagando}
            onClick={() => {
              setApagando(true)
              setFalhou(false)
              listRepository
                .archiveList(lista.id)
                .then(onFechar)
                .catch(() => {
                  setFalhou(true)
                  setApagando(false)
                })
            }}
          >
            Apagar a lista
          </Botao>
        )}
        <Botao type="button" onClick={onFechar}>
          Fechar
        </Botao>
      </div>
    </FolhaDeBaixo>
  )
}
